import { useMemo } from 'react';
import { usePermissions } from '@/features/auth';
import { useVehicleRegistrationQuery } from '@/features/vehiclesearch/hooks/useVehicleRegistrationQuery';
import type { Agreement } from '../types/agreements.types';
import { resolveRegistrationView } from '../utils/registrationView';
import { isServiceableStatus } from '../utils/agreementStatus';

export type RegistrationSource = 'pending' | 'current' | 'snapshot';

export function useAgreementVehicleRegistration(agreement: Agreement | undefined) {
    const { hasPermission } = usePermissions();

    const canReadVehicle = hasPermission({ resource: 'vehicle', action: 'read' });
    const vehicleId = agreement?.vehicleId || undefined;

    // Gated on the permission as well as the id: politeness, not the boundary.
    const live = useVehicleRegistrationQuery(canReadVehicle ? vehicleId : undefined);

    const view = useMemo(
        () => resolveRegistrationView(agreement?.vehicleSnapshot, live.data),
        [agreement?.vehicleSnapshot, live.data],
    );

    // Three states, not two: 'snapshot' is a claim about which record is shown and must
    // not be made while the answer is in flight.
    const source: RegistrationSource = live.data?.vrm
        ? 'current'
        : live.isPending && canReadVehicle && !!vehicleId
          ? 'pending'
          : 'snapshot';

    const isAmendable = agreement !== undefined && isServiceableStatus(agreement.status);

    return {
        view,
        source,
        vehicleId,
        // Hidden while pending: a form seeded from a plate about to be replaced invites a
        // change against the wrong value.
        canChangeRegistration:
            isAmendable &&
            source !== 'pending' &&
            !!vehicleId &&
            hasPermission({ resource: 'vehicle', action: 'change_registration' }),
        isLiveReadFailed: live.isError,
    };
}
